/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, {
    IAny,
    ICommerceApiSettings,
    ICoreContext,
    ICreateActionContext,
    IGeneric,
    IRequestContext,
    generateImageUrl as generateImageUrlFromSdk
} from '@msdyn365-commerce/core';
import {
    AsyncResult,
    AttributeValue,
    CartLine,
    ChannelDeliveryOptionConfiguration,
    ChannelInventoryConfiguration,
    CommerceListLine,
    ProductDimension,
    ProductDimensionType,
    ProductSearchResult,
    QueryResultSettings,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';

import { ProductDetailsCriteria } from '../get-full-products';
import { IKeyValuePair } from './data-structures';
import { ArrayExtensions, StringExtensions } from './extensions';
import { parseSearchData } from './input-data-parser';
import { InventoryLevels } from './product-inventory-information';

export const wrapInResolvedAsyncResult = <T = unknown>(input: T | null | undefined): AsyncResult<T> => {
    return <AsyncResult<T>>{
        status: 'SUCCESS',
        result: input,
        metadata: {}
    };
};

export const wrapInRejectedAsyncResult = <T = unknown>(input: T | null | undefined): AsyncResult<T> => {
    return <AsyncResult<T>>{
        status: 'FAILED',
        result: input,
        metadata: {}
    };
};

export const buildQueryResultSettings = (inputData: ICreateActionContext<IGeneric<IAny>, IGeneric<IAny>>): QueryResultSettings => {
    try {
        const searchInputData = parseSearchData(inputData);
        const search = searchInputData.q;
        if (!search) {
            throw new Error('Query string ?q={searchText} is needed for search actions.');
        }
        const top = (searchInputData.maxItems && Number(searchInputData.maxItems)) || searchInputData.top;
        return { Paging: { Top: top, Skip: searchInputData.skip } };
    } catch {
        return {};
    }
};

/**
 * Utility function to extract the active productId in the following priority:
 * First query param (productId), then UrlToken (itemId), then module config.
 * @param inputData - The Action Input data.
 * @returns Productid or undefined.
 */
export const getSelectedProductIdFromActionInput = (inputData: ICreateActionContext<IGeneric<IAny>>): string | undefined => {
    const context = inputData.requestContext;
    if (context.query?.productId) {
        return context.query.productId;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Exsiting code.
    } else if (
        context.urlTokens &&
        inputData.requestContext.urlTokens.itemId &&
        inputData.requestContext.urlTokens.pageType === 'Product'
    ) {
        return context.urlTokens.itemId;
    } else if (inputData && inputData.config && inputData.config.productId) {
        return inputData.config.productId;
    }
    return undefined;
};

/**
 * Generates a Image URL based on data return from retail server.
 * @param imageUrl The image url returned by Retail Server.
 * @param ctx The request context.
 * @param apiSettings
 */
export const generateImageUrl = (imageUrl: string | undefined, apiSettings: ICommerceApiSettings): string | undefined => {
    return generateImageUrlFromSdk(apiSettings, imageUrl);
};

/**
 * Represents app configuration for dimensions.
 */
export interface IDimensionsConfig {
    dimensionsAsSwatchType?: DimensionTypes[];
    dimensionsInProductCard?: DimensionTypes[];
    inventoryLevel?: InventoryLevels;
    dimensionToPreSelectInProductCard?: DimensionTypes;
    enableStockCheck?: boolean;
    warehouseAggregation?: string;
}

/**
 * Represents app configuration for dimensions.
 */
export interface IDimensionsApp {
    config: IDimensionsConfig;
}

/**
 * Represents product dimension types.
 */
export enum DimensionTypes {
    none = 'none',
    color = 'color',
    configuration = 'configuration',
    size = 'size',
    style = 'style'
}

/**
 * Defines display configuration types.
 */
export enum DimensionSwatchDisplayTypes {
    default = 'default',
    productCard = 'productCard'
}

/**
 * Checks if rendering the particular dimensions is allowed.
 * @param dimensionType - Dimension to be displayed.
 * @param context - Core application context.
 * @param displayType - The swatch location, the component from which it comes from.
 * @returns Updates the state with new product page url.
 */
export const checkIfShouldDisplayAsSwatch = (
    dimensionType: DimensionTypes,
    context: ICoreContext<IDimensionsApp>,
    displayType: DimensionSwatchDisplayTypes = DimensionSwatchDisplayTypes.default
): boolean => {
    // Additional checks for the product card since it has a separate config setting.
    if (displayType === DimensionSwatchDisplayTypes.productCard) {
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        if (
            !ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) ||
            dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) ||
            !dimensionsToDisplayOnProductCard.includes(dimensionType)
        ) {
            return false;
        }
    }

    const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
    return (
        ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
        !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none) &&
        dimensionsToDisplayAsSwatch.includes(dimensionType)
    );
};

/**
 * Generates an image URL for a swatch.
 * @param imageUrl - The image url returned by Retail Server.
 * @param apiSettings - The request context.
 * @returns Full image url.
 */
export const generateSwatchImageUrl = (imageUrl: string, apiSettings: ICommerceApiSettings): string => {
    if (imageUrl.startsWith('http')) {
        return imageUrl;
    }

    return apiSettings.baseImageUrl + encodeURIComponent(imageUrl);
};

/**
 * Generates a Image URL for a product based on data return from retail server.
 * @param product The Product returned by Retail Server.
 * @param ctx The request context.
 * @param apiSettings
 */
export const generateProductImageUrl = (
    product: SimpleProduct | ProductSearchResult,
    apiSettings: ICommerceApiSettings
): string | undefined => {
    return generateImageUrl(product.PrimaryImageUrl, apiSettings);
};

/**
 * Creates a CartLine object from the passed data.
 * @param product The product.
 * @param quantity The quantity.
 * @param catalogId The catalog.
 */
export const buildCartLine = (product: SimpleProduct, quantity?: number, catalogId?: number): CartLine => {
    return {
        CatalogId: catalogId || 0,
        Description: product.Description,

        // TODO: Investigate this value and what it represents
        EntryMethodTypeValue: 3,
        ItemId: product.ItemId,
        ProductId: product.RecordId,
        Quantity: quantity || 1,
        TrackingId: '',
        UnitOfMeasureSymbol: product.DefaultUnitOfMeasure
    };
};

/**
 * Creates a CommerceListLine (also know as WishlistLine) object from the passed data.
 * @param productId The RecordId of the Product to be added.
 * @param customerId The account number of the customer.
 * @param wishlistId The Id of the commerce list.
 */
export const buildWishlistLine = (productId: number, customerId: string, wishlistId: number): CommerceListLine => {
    return {
        CommerceListId: wishlistId,
        ProductId: productId,
        CustomerId: customerId
    };
};

/**
 * Utility function to prepare the product details criteria before getting full product:
 * @param inputData The Action Input data
 */
export const getProductDetailsCriteriaFromActionInput = (inputData: ICreateActionContext<IGeneric<IAny>>): ProductDetailsCriteria => {
    if (inputData && inputData.config) {
        return {
            getPrice: !inputData.config.hidePrice,
            getRating: !inputData.config.hideRating
        };
    }
    return {
        getPrice: true,
        getRating: true
    };
};

/**
 * Transforms search text into the expected 'search terms' format, expected by refiner APIs.
 * @param searchText Free-form text used for searching for products or categories of products.
 */
export const ensureSearchTextIsFormedProperly = (searchText: string): string => {
    const prefix = searchText.startsWith("'") ? '' : "'";
    const suffix = searchText.endsWith("'") ? '' : "'";
    return `${prefix}${searchText}${suffix}`;
};

export const ensureSafeSearchText = (searchText: string): string => {
    return searchText.replace(/[^\d\sA-Za-z]+/g, '');
};

/**
 * @deprecated Please use `generateCacheKey` instead.
 */
export const buildCacheKey = (base: string, apiSettings: ICommerceApiSettings, locale?: string): string => {
    return `${base}-chanId:${apiSettings.channelId}-catId:${apiSettings.catalogId}${locale ? `-${locale}` : ''}`;
};

/**
 * Represents a list of keys for general parameters used in the cache key.
 */
enum DefaultCacheKeyParameterKeys {
    channelIdKey = 'CHANNELID',
    appSettingsCatalogIdKey = 'APPSETTINGSCATALOGID'
}

/**
 * Generates a key from set of arguments
 * @param apiSettings - Request context API settings.
 * @param entries - Cache key parameters.
 * @returns String which represents the cache key.
 */
export const generateCacheKey = (
    apiSettings?: ICommerceApiSettings,
    ...entries: IKeyValuePair<string, TKeyTypes | TKeyTypes[]>[]
): string => {
    const parsedEntries: IKeyValuePair<string, TKeyTypes | TKeyTypes[]>[] = entries.map(pair => {
        return { key: pair.key.toLocaleUpperCase(), value: pair.value };
    });

    const cacheKeyArgumentsKeys = parsedEntries.map(pair => pair.key);

    if (apiSettings) {
        if (!cacheKeyArgumentsKeys.includes(DefaultCacheKeyParameterKeys.channelIdKey)) {
            parsedEntries.push({ key: DefaultCacheKeyParameterKeys.channelIdKey, value: apiSettings.channelId });
        }

        if (!cacheKeyArgumentsKeys.includes(DefaultCacheKeyParameterKeys.appSettingsCatalogIdKey) && apiSettings.catalogId) {
            parsedEntries.push({ key: DefaultCacheKeyParameterKeys.appSettingsCatalogIdKey, value: apiSettings.catalogId });
        }
    }

    return parsedEntries
        .sort((a, b) => a.key.localeCompare(b.key))
        .map(pair => `${pair.key}:{${pair.value}}`)
        .join('-');
};

export const buildCacheKeyWithUrlTokens = (base: string, requestContext: IRequestContext): string => {
    const urlTokens = requestContext.urlTokens;
    const defaultCacheKey = buildCacheKey(base, requestContext.apiSettings, requestContext.locale);
    return urlTokens ? `${defaultCacheKey}-${urlTokens.itemId}-${urlTokens.recordId}-${urlTokens.pageType}` : defaultCacheKey;
};

/**
 * Generates a key from set of arguments as inputs.
 *
 * @param args Argument list of pivots to generate key from.
 * @param handler Handler function for null/undefined values.
 */

type TKeyTypes = string | number | boolean | null | undefined;
interface IGenerateKeyOptions {
    separator?: string;
    handler?(input: null | undefined): string;
}
export const generateKey = (args: TKeyTypes[], options?: IGenerateKeyOptions): string => {
    const { handler, separator }: IGenerateKeyOptions = { ...{ separator: '-', handler: undefined }, ...(options || {}) };
    return args
        .map(arg => {
            if (arg === null || arg === undefined) {
                if (handler) {
                    return handler(arg);
                }
            }
            return arg;
        })
        .join(separator);
};

/**
 * Gets the fall back image url for a variant image.
 * @param itemId Item id of a product.
 * @param apiSettings Api setting from request context.
 */
export const getFallbackImageUrl = (
    itemId: string | undefined,
    apiSettings: ICommerceApiSettings,
    isOmniFeatureEnabled?: boolean
): string | undefined => {
    if (!itemId || isOmniFeatureEnabled) {
        return '';
    }

    const parsedItemId = itemId.trim().split(' ')[0];

    const productUrl = `Products/${parsedItemId}_000_001.png`;
    return generateImageUrl(productUrl, apiSettings);
};

/**
 * Converts retail proxy product dimension type to display dimension type.
 * @param productDimensionType - Product dimension type from retail proxy.
 * @returns Local dimension type.
 */
export const convertProductDimensionTypeToDimensionTypes = (productDimensionType: ProductDimensionType): DimensionTypes => {
    switch (productDimensionType) {
        case ProductDimensionType.Color:
            return DimensionTypes.color;
        case ProductDimensionType.Configuration:
            return DimensionTypes.configuration;
        case ProductDimensionType.Size:
            return DimensionTypes.size;
        case ProductDimensionType.Style:
            return DimensionTypes.style;
        default:
            return DimensionTypes.none;
    }
};

/**
 * Converts display dimension type to retail proxy product dimension type.
 * @param dimensionType - Local dimension type.
 * @returns Product dimension type from retail proxy.
 */
export const convertDimensionTypeToProductDimensionType = (dimensionType: DimensionTypes): ProductDimensionType => {
    switch (dimensionType) {
        case DimensionTypes.color:
            return ProductDimensionType.Color;
        case DimensionTypes.configuration:
            return ProductDimensionType.Configuration;
        case DimensionTypes.size:
            return ProductDimensionType.Size;
        case DimensionTypes.style:
            return ProductDimensionType.Style;
        default:
            return ProductDimensionType.None;
    }
};

/**
 * Sets dimensions to url.
 * @param inputUrl - Url to update.
 * @param productDimensions - Dimensions to set.
 */
export const setDimensionValuesToQuery = (inputUrl: URL, productDimensions: ProductDimension[]): void => {
    for (const dimension of productDimensions) {
        const dimensionValue = dimension.DimensionValue?.Value;
        if (!StringExtensions.isNullOrWhitespace(dimensionValue)) {
            inputUrl.searchParams.set(convertProductDimensionTypeToDimensionTypes(dimension.DimensionTypeValue), dimensionValue!);
        }
    }

    if (msdyn365Commerce.isBrowser) {
        window.history.replaceState(window.history.state, '', inputUrl.toString().replace(inputUrl.host, window.location.host));
    }
};

/**
 * Retrieves dimension value stored in url.
 * @param urlParameters - Url parameters.
 * @param productDimensionType - The dimension type to retrieve.
 * @returns Product dimension item.
 */
const parseDimensionFromUrl = (
    urlParameters: URLSearchParams,
    productDimensionType: ProductDimensionType
): ProductDimension | undefined => {
    const localDimensionType = convertProductDimensionTypeToDimensionTypes(productDimensionType);
    const value = urlParameters.get(localDimensionType);
    if (!value) {
        return undefined;
    }
    return {
        DimensionTypeValue: productDimensionType,
        DimensionValue: {
            RecordId: 0,
            Value: value
        }
    };
};

/**
 * Retrieves all dimensions from URL.
 * @param urlParameters - Url parameters.
 * @returns Product dimensions stored in url.
 */
const parseDimensionsFromUrl = (urlParameters: URLSearchParams): ProductDimension[] => {
    const dimensionsToParse: ProductDimensionType[] = [
        ProductDimensionType.Color,
        ProductDimensionType.Configuration,
        ProductDimensionType.Size,
        ProductDimensionType.Style
    ];
    const parsedDimensions = dimensionsToParse.map(dimension => parseDimensionFromUrl(urlParameters, dimension));
    return ArrayExtensions.validValues(parsedDimensions);
};

/**
 * Utility function to extract the dimension values from input url querystring.
 * @param inputUrl -  The request url.
 * @returns The selected dimension values.
 */
export const getDimensionValuesFromQuery = (inputUrl: URL): ProductDimension[] => {
    const urlWithIgnoredCase = new URL(inputUrl.toString().toLocaleLowerCase());
    const urlParameters = urlWithIgnoredCase.searchParams;
    const dimensions: ProductDimension[] = parseDimensionsFromUrl(urlParameters);
    return dimensions;
};

/**
 * Get attribute values by channel inventory configuration.
 * @param attributeValues - The attributeValues.
 * @param channelInventoryConfiguration - The channelInventoryConfiguration.
 * @returns The attributeValues filter by channel inventory configuration.
 */
export const getAttributeValuesByConfiguration = (
    attributeValues: AttributeValue[] | null,
    channelInventoryConfiguration: ChannelInventoryConfiguration | null
): AttributeValue[] => {
    if (!attributeValues) {
        return [];
    }
    if (!channelInventoryConfiguration) {
        return attributeValues;
    }

    return attributeValues.filter(
        attribute =>
            attribute.RecordId !== channelInventoryConfiguration.InventoryProductAttributeRecordId &&
            attribute.RecordId !== channelInventoryConfiguration.ProductAvailabilitySortableAttributeRecordId
    );
};

/**
 * Utility function to remove domain querystring parameter from url.
 * @param inputUrl -  The request url.
 * @returns The yrl after dropping domain querystring.
 */
export const removeDomainQspFromUrl = (inputUrl: string, requestContext: IRequestContext): string => {
    if (requestContext?.features && requestContext.features.remove_domain_qsp) {
        const Url = new URL(inputUrl);
        const urlParams: URLSearchParams = new URLSearchParams(Url.search);
        if (urlParams.has('domain')) {
            urlParams.delete('domain');
        }
        Url.search = urlParams.toString();
        return Url.toString();
    }
    return inputUrl;
};

/**
 * Utility function to validate catalog Id for B2B user .
 * @param catalogId -  The catalog.
 */
export const validateCatalogId = (catalogId?: number | undefined) => {
    if (catalogId === -1) {
        throw new Error(
            'Catalog id is -1. It means no valid catalogs were found for the user. Make sure the user has some valid catalogs assigned.'
        );
    }
};

/**
 * Utility function to check deliveryMode .
 * @param cartline - CartLine item.
 * @param retailMulitplePickupFeatureState - pickUp feature state, default to false.
 * @param channelDeliveryOptionConfig - Channel Delivery Option Configuration.
 * @param pickupDeliveryModeCode -pickUp delivery mode code string.
 * @returns true id cartline item is pickUp item else false.
 */
export const getDeliveryMode = (
    cartLine: CartLine,
    featureSate: boolean = false,
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,
    pickupDeliveryMode?: string
) => {
    if (!featureSate) {
        return cartLine.DeliveryMode === pickupDeliveryMode;
    }
    return (
        cartLine.DeliveryMode ===
        channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLine.DeliveryMode)
    );
};

/**
 * Utility function to check if cartline item is pickupitem .
 * @param cartline - CartLine item.
 * @param retailMulitplePickupFeatureState - pickUp feature state.
 * @param channelDeliveryOptionConfig - Channel Delivery Option Configuration.
 * @param pickupDeliveryModeCode -pickUp delivery mode code string.
 * @returns true id cartline item is pickUp item else false.
 */
export const isCartLineItemPickUpType = (
    cartline: CartLine,
    retailMulitplePickupFeatureState: boolean = false,
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,
    pickupDeliveryModeCode?: string
): boolean => {
    // check if cartlineitem is pickup item
    if (
        cartline.DeliveryMode &&
        cartline.DeliveryMode !== '' &&
        getDeliveryMode(cartline, retailMulitplePickupFeatureState, channelDeliveryOptionConfig, pickupDeliveryModeCode)
    ) {
        return true;
    }
    return false;
};
